import { motion, useReducedMotion } from "framer-motion";
import LeadForm from "../LeadForm";
import { IMAGES } from "../../data/content";
import { HERO_VIDEO_URL, DATE_DEBUT, DATE_FIN } from "../../config";

const line = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero() {
  const reduced = useReducedMotion();

  return (
    <section
      id="hero"
      className="relative min-h-[92vh] md:min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Média de fond : vidéo optionnelle, sinon grande photographie animée */}
      <div className={`hero-media ${HERO_VIDEO_URL ? "" : "hero-media--anim"}`}>
        {HERO_VIDEO_URL ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={IMAGES.heroDive}
            preload="none"
          >
            <source src={HERO_VIDEO_URL} type="video/mp4" />
          </video>
        ) : (
          <img
            src={IMAGES.heroDive}
            alt="Vue aérienne d'une personne entrant dans une mer turquoise profonde au lever du soleil"
            fetchPriority="high"
          />
        )}
      </div>
      <div className="hero-overlay" />

      <div className="relative z-[3] w-full max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Colonne gauche — message */}
          <div className="max-w-xl">
            <motion.p
              {...line(0.1)}
              initial={reduced ? { opacity: 0 } : line(0.1).initial}
              className="eyebrow text-aqua mb-5"
            >
              UNE EXPÉRIENCE GRATUITE DE 7 JOURS
            </motion.p>

            <h1 className="font-display font-medium leading-[0.86] tracking-tight text-pearl">
              {["LA", "TRAVERSÉE"].map((word, i) => (
                <span key={word} className="block">
                  <span className="inline-block overflow-hidden align-bottom pb-2">
                    <motion.span
                      className="inline-block text-[clamp(3rem,8vw,6.5rem)] whitespace-nowrap"
                      initial={reduced ? { opacity: 0 } : { y: "115%" }}
                      animate={reduced ? { opacity: 1 } : { y: "0%" }}
                      transition={{
                        duration: 1.1,
                        delay: 0.2 + i * 0.12,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      {word}
                    </motion.span>
                  </span>
                </span>
              ))}
            </h1>

            <motion.p
              {...line(0.5)}
              className="mt-6 font-display text-[1.5rem] md:text-[1.9rem] leading-snug text-pearl/95"
            >
              7 jours pour retrouver la part de toi qui sait encore sentir, choisir et
              avancer.
            </motion.p>

            <motion.p {...line(0.65)} className="mt-4 text-pearl/70 text-base leading-relaxed">
              Une expérience guidée par le vivant, la conscience et l'énergie symbolique
              des Maîtres Dauphins.
            </motion.p>

            <motion.div {...line(0.8)} className="mt-7 flex flex-col gap-3">
              <p className="font-display text-xl md:text-2xl text-aqua">
                Du {DATE_DEBUT} au {DATE_FIN}
              </p>
              <p className="text-pearl/70 text-sm tracking-wide">
                3 grandes soirées en ligne • Expériences guidées • Groupe privé • Replays
                inclus
              </p>
              <p className="mt-2 text-pearl/85 italic font-display text-lg">
                Ton premier pas commence ici.
              </p>
            </motion.div>
          </div>

          {/* Colonne droite — carte d'inscription */}
          <motion.div
            initial={reduced ? { opacity: 0 } : { opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="w-full lg:justify-self-end lg:max-w-md"
          >
            <LeadForm variant="hero" formLocation="hero" />
          </motion.div>
        </div>
      </div>

      <div className="hero-scroll-hint">
        <span>Descendre</span>
        <span className="hero-scroll-line" />
      </div>
    </section>
  );
}
